/**
 * src/views/UsersView.tsx
 * Admin User Management Screen with debtor/creditor summaries and user creation
 */

import React, { useState } from 'react';
import { Users, UserPlus, Search, TrendingUp, TrendingDown, CheckCircle2, RefreshCw, Sparkles } from 'lucide-react';
import { useUsers } from '../hooks/useUsers';
import { useToast } from '../context/ToastContext';
import { UserCard } from '../components/users/UserCard';
import { AddUserModal } from '../components/users/AddUserModal';
import { ChangePasswordModal } from '../components/users/ChangePasswordModal';
import { DebtMinimizerSheet } from '../components/settlement/DebtMinimizerSheet';
import { SettleUpModal } from '../components/settlement/SettleUpModal';
import { GlassCard } from '../components/ui/GlassCard';
import { GlassButton } from '../components/ui/GlassButton';
import { api, type UserItem } from '../services/api';

interface UsersViewProps {
  onViewUserStatement?: (userId: number) => void;
}

export const UsersView: React.FC<UsersViewProps> = ({ onViewUserStatement }) => {
  const { users, isLoading, refetch, createUser, updateUser } = useUsers();
  const { success, error } = useToast();

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'active' | 'deactivated' | 'debtors' | 'creditors'>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState<UserItem | null>(null);

  // Settlement features state
  const [isMinimizerOpen, setIsMinimizerOpen] = useState(false);
  const [settlingUser, setSettlingUser] = useState<UserItem | null>(null);
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);

  // Calculate metrics
  const activeMembers = users.filter(u => u.is_active !== 0);
  const deactivatedMembers = users.filter(u => u.is_active === 0);
  const creditors = users.filter(u => (u.current_balance ?? 0) > 0);
  const debtors = users.filter(u => (u.current_balance ?? 0) < 0);
  const settled = users.filter(u => (u.current_balance ?? 0) === 0);

  const totalCreditorSum = creditors.reduce((sum, u) => sum + (u.current_balance ?? 0), 0);
  const totalDebtorSum = debtors.reduce((sum, u) => sum + Math.abs(u.current_balance ?? 0), 0);

  // Filtered users
  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = 
      u.username.toLowerCase().includes(q) ||
      Boolean(u.full_name && u.full_name.toLowerCase().includes(q)) ||
      Boolean(u.whatsapp_phone && u.whatsapp_phone.includes(q)) ||
      Boolean(u.upi_id && u.upi_id.toLowerCase().includes(q));
    if (!matchesSearch) return false;

    if (filterType === 'active') return u.is_active !== 0;
    if (filterType === 'deactivated') return u.is_active === 0;

    const bal = u.current_balance ?? 0;
    if (filterType === 'creditors') return bal > 0;
    if (filterType === 'debtors') return bal < 0;
    return true;
  });

  const handleToggleUserStatus = async (targetUser: UserItem) => {
    const nextActive = targetUser.is_active === 0 ? 1 : 0;
    try {
      await updateUser(targetUser.id, { is_active: nextActive });
      success(
        nextActive === 1 
          ? `User "${targetUser.username}" reactivated and restored to ledger!`
          : `User "${targetUser.username}" deactivated. Login blocked and removed from bill splits.`,
        nextActive === 1 ? 'User Reactivated' : 'User Deactivated'
      );
    } catch (err: any) {
      error(err.message || 'Failed to update user status');
    }
  };

  const handleCreateOrEditSubmit = async (data: any) => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, data);
        success(`User ${data.username || editingUser.username} updated successfully!`, 'User Updated');
      } else {
        await createUser(data);
        success(`New user account "${data.username}" created!`, 'User Created');
      }
      setEditingUser(null);
    } catch (err: any) {
      error(err.message || 'Operation failed');
      throw err;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Top Header */}
      <div className="flex-between" style={{ flexWrap: 'wrap', gap: '14px' }}>
        <div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={26} color="var(--indigo-light)" />
            <span>User Management</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '4px' }}>
            {users.length} active users registered in CKAudit system
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <GlassButton
            variant="default"
            icon={<Sparkles size={16} color="var(--ios-purple)" />}
            onClick={() => setIsMinimizerOpen(true)}
            title="Calculate optimal settlements across all members"
          >
            Debt Minimizer
          </GlassButton>

          <GlassButton
            variant="ghost"
            icon={<RefreshCw size={16} />}
            onClick={refetch}
            title="Refresh Users"
          >
            Refresh
          </GlassButton>

          <GlassButton
            variant="primary"
            icon={<UserPlus size={18} />}
            onClick={() => {
              setEditingUser(null);
              setIsAddModalOpen(true);
            }}
          >
            Add New User
          </GlassButton>
        </div>
      </div>

      {/* Financial Health Summary Cards - iOS Inset Highlights */}
      <div className="grid-cols-3" style={{ gap: '12px' }}>
        {/* Total Debtor Deficit */}
        <div className="ios-inset-group" style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <TrendingDown size={14} color="var(--ios-red)" />
            <span>Outstanding Debtors ({debtors.length})</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--ios-red)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            -₹{totalDebtorSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px', fontWeight: 500 }}>
            Receivable from members
          </div>
        </div>

        {/* Total Creditor Surplus */}
        <div className="ios-inset-group" style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <TrendingUp size={14} color="var(--ios-green)" />
            <span>Net Creditors ({creditors.length})</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--ios-green)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            +₹{totalCreditorSum.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px', fontWeight: 500 }}>
            Surplus credit balance
          </div>
        </div>

        {/* Settled Accounts */}
        <div className="ios-inset-group" style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
            <CheckCircle2 size={14} color="var(--ios-blue)" />
            <span>Settled ({settled.length})</span>
          </div>
          <div className="num-font" style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '4px', letterSpacing: '-0.02em' }}>
            {settled.length} Members
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px', fontWeight: 500 }}>
            Exact zero balance accounts
          </div>
        </div>
      </div>

      {/* Filter & Search Bar - iOS Inset Group */}
      <div className="ios-inset-group" style={{ padding: '12px 16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
          <input
            type="text"
            className="glass-input"
            style={{ paddingLeft: '36px', paddingRight: '12px', paddingTop: '7px', paddingBottom: '7px', fontSize: '0.85rem', borderRadius: '10px' }}
            placeholder="Search members by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Status Filter Toggle - iOS Segmented Capsule */}
        <div className="ios-segmented-control" style={{ padding: '3px', flexWrap: 'wrap', gap: '2px' }}>
          <button
            type="button"
            onClick={() => setFilterType('all')}
            className={`ios-segment-button ${filterType === 'all' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            All ({users.length})
          </button>

          <button
            type="button"
            onClick={() => setFilterType('active')}
            className={`ios-segment-button ${filterType === 'active' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            Active ({activeMembers.length})
          </button>

          <button
            type="button"
            onClick={() => setFilterType('deactivated')}
            className={`ios-segment-button ${filterType === 'deactivated' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            Deactivated ({deactivatedMembers.length})
          </button>

          <button
            type="button"
            onClick={() => setFilterType('debtors')}
            className={`ios-segment-button ${filterType === 'debtors' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            Debtors ({debtors.length})
          </button>

          <button
            type="button"
            onClick={() => setFilterType('creditors')}
            className={`ios-segment-button ${filterType === 'creditors' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            Creditors ({creditors.length})
          </button>
        </div>
      </div>

      {/* Users Grid */}
      {isLoading && users.length === 0 ? (
        <div className="glass-card flex-center" style={{ padding: '60px', color: 'var(--text-secondary)' }}>
          <RefreshCw size={24} className="animate-spin" style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} />
          <span>Loading user accounts...</span>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="glass-card flex-center" style={{ padding: '60px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          No users match the search criteria.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}
        >
          {filteredUsers.map(u => (
            <UserCard
              key={u.id}
              user={u}
              onEdit={(userToEdit) => {
                setEditingUser(userToEdit);
                setIsAddModalOpen(true);
              }}
              onChangePassword={(userToReset) => setPasswordChangeUser(userToReset)}
              onToggleStatus={handleToggleUserStatus}
              onViewStatement={onViewUserStatement}
              onSettleUp={(selectedMember) => {
                setSettlingUser(selectedMember);
                setIsSettleModalOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Add / Edit User Modal */}
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleCreateOrEditSubmit}
        editUser={editingUser}
      />

      {/* Change / Reset User Password Modal (Admin) */}
      <ChangePasswordModal
        isOpen={Boolean(passwordChangeUser)}
        onClose={() => setPasswordChangeUser(null)}
        targetUser={passwordChangeUser}
        onSuccess={refetch}
      />

      {/* Smart Debt Minimizer Sheet */}
      <DebtMinimizerSheet
        isOpen={isMinimizerOpen}
        onClose={() => setIsMinimizerOpen(false)}
        users={users}
        onInitiateSettlement={(transfer) => {
          setIsMinimizerOpen(false);
          setSettlingUser(transfer.fromUser);
          setIsSettleModalOpen(true);
        }}
      />

      {/* Settle Up Google Pay Modal */}
      {settlingUser && (
        <SettleUpModal
          isOpen={isSettleModalOpen}
          onClose={() => {
            setIsSettleModalOpen(false);
            setSettlingUser(null);
          }}
          memberId={settlingUser.id}
          memberName={settlingUser.username}
          amountOwed={Math.abs(settlingUser.current_balance ?? 0)}
          adminUpiId={users.find(u => u.role === 'admin')?.upi_id || 'chintankheradia123-1@okaxis'}
          userUpiId={settlingUser.upi_id}
          isPayout={(settlingUser.current_balance ?? 0) > 0}
          onSettlementSubmitted={async (data) => {
            const isMemberCreditor = (settlingUser.current_balance ?? 0) > 0;
            await api.approvals.create({
              user_id: data.user_id,
              transaction_type: isMemberCreditor ? 'paid' : 'receive',
              amount: data.amount,
              description: data.description,
              category_name: 'Cash',
              transaction_date: new Date().toISOString(),
              receipt_url: data.receipt_url || undefined
            });
            refetch();
            success(isMemberCreditor ? 'Payout settlement submitted! Awaiting review.' : 'Payment notification submitted! It is now Under Review.', 'Under Review');
          }}
        />
      )}
    </div>
  );
};
